<template>
    <div class="editable-group" :class="{ 'editable-group--editing': isEditingGroup }">
        <CollapsibleWidget
            :is-opened="false"
            class="editable-group__collapsible-widget"
            @onChange="onCollapsibleWidgetInput"
        >
            <template #name>
                <span class="capitalize-text">{{ groupLabel }}</span>
            </template>

            <template #controls>
                <Poptip
                    v-if="hasImportedFeedValues"
                    v-model="isPoptipActive"
                    placement="bottom-end"
                    class="editable-group__feed-more"
                    trigger="hover"
                >
                    <div data-testid="editable-group__more-menu">
                        <div class="editable-group__more-icon">
                            <Icon type="ios-more" @click.stop="togglePoptip" />
                        </div>
                    </div>

                    <template #content>
                        <div
                            class="editable-group__action"
                            data-testid="editable-group__refresh"
                            @click.stop="refreshFeed"
                        >
                            <Icon type="md-refresh" />
                            <span class="editable-group-actions__refresh">Refresh</span>
                        </div>

                        <div
                            class="editable-group__action"
                            data-testid="editable-group__import"
                            @click.stop="importFeed"
                        >
                            <Icon type="md-cloud-upload" />
                            <span class="editable-group-actions__refresh">Replace</span>
                        </div>

                        <div
                            class="editable-group__action"
                            data-testid="editable-group__delete"
                            @click.stop="toggleDeleteFeedModal"
                        >
                            <Icon type="ios-trash" />
                            <span class="editable-group-actions__delete">Delete</span>
                        </div>
                    </template>
                </Poptip>

                <Icon v-else class="editable-group__feed-upload" type="md-cloud-upload" @click.stop="importFeed" />
            </template>

            <div class="editable-group__body">
                <RadioGroup
                    v-if="editableGroupValuesForGroup.length"
                    class="editable-group__values"
                    vertical
                    :model-value="groupValue"
                >
                    <div
                        v-for="editableGroupValueId in editableGroupValuesForGroup"
                        :key="editableGroupValueId"
                        class="editable-group__value-wrapper"
                        data-testid="editable-group__value-wrapper"
                    >
                        <div v-if="isEditing !== editableGroupValueId">
                            <Radio
                                :label="getEditableGroupValueById(editableGroupValueId)"
                                @click.stop.prevent="onGroupValueChange(editableGroupValueId)"
                            />

                            <div v-if="canManageGroupValues" class="editable-group__value-actions">
                                <Icon
                                    type="md-create"
                                    data-testid="editable-group__value-actions--edit"
                                    @click="
                                        showEditingForm(
                                            editableGroupValueId,
                                            getEditableGroupValueById(editableGroupValueId)
                                        )
                                    "
                                />

                                <Icon
                                    type="md-copy"
                                    data-testid="editable-group__value-actions--duplicate"
                                    @click="duplicateGroupValue(editableGroupValueId)"
                                />

                                <Icon
                                    type="md-trash"
                                    data-testid="editable-group__value-actions--delete"
                                    @click="
                                        onGroupAlertOpen(
                                            editableGroupValueId,
                                            getEditableGroupValueById(editableGroupValueId)
                                        )
                                    "
                                />
                            </div>
                        </div>

                        <div v-else class="editable-group__add">
                            <Input
                                :id="`editingInput-${editableGroupValueId}`"
                                v-model="editingValue"
                                @onEnter="
                                    editValue(editableGroupValueId, getEditableGroupValueById(editableGroupValueId))
                                "
                            />
                            <Icon
                                type="md-checkmark"
                                @click="
                                    editValue(editableGroupValueId, getEditableGroupValueById(editableGroupValueId))
                                "
                            />
                            <Icon type="md-close" @click="cancelEdit" />
                        </div>
                    </div>
                </RadioGroup>

                <div v-if="canManageGroupValues">
                    <template v-if="!isAdding">
                        <div class="editable-group__controls" @click.stop="showForm">
                            <Icon type="md-add" class="show-form" />
                            <span class="editable-group__controls--tip">Add new</span>
                        </div>
                    </template>

                    <div v-else class="editable-group__add">
                        <Input ref="addingInput" v-model="newValue" @onEnter="addValue" />
                        <Icon type="md-checkmark" @click="addValue" />
                        <Icon type="md-close" @click="cancelAdd" />
                    </div>
                </div>
            </div>
        </CollapsibleWidget>

        <AlertModal
            v-model="removeFeedModal"
            title="Are you sure?"
            content="If you decide to delete this feed, you'll need to add or upload a new one."
            :warning="false"
            class="remove-feed__modal"
        >
            <template #footer>
                <Button
                    class="campaign-edit__confirm-unsaved-changes-save-button"
                    type="error"
                    @click="deleteExternalFeed"
                >
                    Delete
                </Button>
                <Button @click.stop="toggleDeleteFeedModal">Cancel</Button>
            </template>
        </AlertModal>

        <div v-if="removeGroupValueModal">
            <AlertModal
                v-if="editableGroupValuesForGroup.length === 1"
                v-model="removeGroupValueModal"
                header="Cannot remove the group value"
                content="Group has to contain at least one group value"
                :warning="true"
                :show-cancel="false"
                :on-ok="onGroupAlertClose"
            />
            <AlertModal
                v-else
                v-model="removeGroupValueModal"
                :header="'Remove group value'"
                :title="'Are you sure?'"
                :content="'Removing the group value will delete all content edits for that group value.'"
                :ok-text="'Remove'"
                :on-cancel="onGroupAlertClose"
                :on-ok="removeGroupValue"
            />
        </div>
    </div>
</template>

<script lang="ts">
import AlertModal from "@/components/Modal/Alert.vue";
import CollapsibleWidget from "@/components/Campaign/widgets/CollapsibleWidget.vue";

import { EditableGroupEvent } from "@/enums/editables";
import { formatGroupName } from "@/utils";

export default {
    name: "EditableGroup",
    components: {
        CollapsibleWidget,
        AlertModal
    },

    props: {
        groupName: {
            type: String,
            required: true
        }
    },

    emits: [
        EditableGroupEvent.Add,
        EditableGroupEvent.Duplicate,
        EditableGroupEvent.Edit,
        EditableGroupEvent.Remove,
        EditableGroupEvent.Update,
        EditableGroupEvent.ImportFeed,
        EditableGroupEvent.RefreshFeed,
        EditableGroupEvent.DeleteFeed
    ],

    data() {
        return {
            isAdding: false,
            isEditing: null as null | string,
            removeGroupValueModal: false,
            groupValueToRemove: null as { groupValueId: string; groupValue: string } | null,
            newValue: "",
            editingValue: "",
            isPoptipActive: false,
            removeFeedModal: false
        };
    },

    computed: {
        campaignId() {
            return this.$route.params.campaignId as string;
        },

        sandboxMode() {
            return this.$store.state.editor.isSandboxMode;
        },

        canManageGroupValues() {
            return (
                (this.sandboxMode && this.$auth.userCan(this.$auth.Actions.CanUseSandbox)) ||
                this.$auth.userCan(this.$auth.Actions.CanManageGroupValue, {
                    clientId: this.clientId,
                    campaignId: this.campaignId
                })
            );
        },

        clientId() {
            return this.$route.params.clientId as string;
        },

        editableGroups() {
            return this.$store.state.campaign.normalized.editableGroups;
        },

        editableGroupValues() {
            return this.$store.state.campaign.editableGroupValues;
        },

        editableGroupValuesForGroup() {
            return this.editableGroups[this.groupName].editableGroupValues;
        },

        groupLabel() {
            return formatGroupName(this.groupName);
        },

        isEditingGroup() {
            return this.groupName === this.$store.state.editor.editingGroupName;
        },

        selectedGroupValues() {
            return this.$store.state.editor.selectedGroupValues;
        },

        hasImportedFeedValues() {
            const savedGroup = this.$store.state.campaign.groupFeedMappings?.find(
                mapping => mapping.group === this.groupName
            );

            return !!savedGroup?.feedUrl;
        },

        groupValue() {
            if (this.groupName in this.selectedGroupValues) {
                const editableGroupGroupId = this.selectedGroupValues[this.groupName].groupValueId;

                if (editableGroupGroupId && this.editableGroupValues[editableGroupGroupId]) {
                    return this.editableGroupValues[editableGroupGroupId].value;
                }
            }

            return null;
        }
    },

    methods: {
        addValue() {
            const value = this.newValue.trim();

            if (value === "") {
                this.cancelAdd();
                return;
            }

            const usedValues = this.editableGroups[this.groupName].editableGroupValues.map(
                editableGroupValueId => this.editableGroupValues[editableGroupValueId].value
            );

            if (usedValues.includes(value)) {
                this.$snackbar.warning("The value needs to be unique");
                return;
            }

            this.$emit(EditableGroupEvent.Add, {
                groupName: this.groupName,
                value
            });

            this.isAdding = false;
        },

        duplicateGroupValue(groupValueId: string) {
            this.$emit(EditableGroupEvent.Duplicate, {
                groupName: this.groupName,
                groupValueId
            });
        },

        async showEditingForm(groupValueId: string, groupValue: string) {
            this.editingValue = groupValue;
            this.isEditing = groupValueId;

            await this.$nextTick();

            const input = document.querySelector(`#editingInput-${groupValueId} input`) as HTMLInputElement;

            if (input) {
                input.focus();
            }
        },

        async showForm() {
            this.newValue = "";
            this.isAdding = true;

            await this.$nextTick();

            if (this.$refs.addingInput) {
                (this.$refs.addingInput as HTMLInputElement).focus();
            }
        },

        cancelAdd() {
            this.isAdding = false;
        },

        cancelEdit() {
            this.isEditing = null;
        },

        editValue(groupValueId: string, groupValue: string) {
            if (this.isEditing !== groupValueId) {
                return;
            }

            const value = this.editingValue.trim();

            if (value === "" || value === groupValue) {
                this.cancelEdit();
                return;
            }

            const usedValues = this.editableGroups[this.groupName].editableGroupValues.map(
                editableGroupValueId => this.editableGroupValues[editableGroupValueId].value
            );

            if (usedValues.includes(value)) {
                this.$snackbar.warning("The value needs to be unique");
                return;
            }

            this.$emit(EditableGroupEvent.Edit, {
                groupName: this.groupName,
                groupValue,
                editableGroupValues: [
                    {
                        editableGroupValueId: groupValueId,
                        value
                    }
                ]
            });

            this.cancelEdit();
        },

        getEditableGroupValueById(editableGroupValueId: string) {
            return this.editableGroupValues[editableGroupValueId].value;
        },

        importFeed() {
            this.$emit("importFeed", this.groupName);
        },

        refreshFeed() {
            this.$emit("refreshFeed", this.groupName);
        },

        onGroupValueChange(editableGroupValueId: string) {
            const payload = {
                groupValueId: editableGroupValueId,
                groupName: this.groupName
            };

            this.$emit(EditableGroupEvent.Update, payload);
        },

        onGroupAlertOpen(groupValueId: string, groupValue: string) {
            this.removeGroupValueModal = true;
            this.groupValueToRemove = { groupValueId, groupValue };
        },

        onGroupAlertClose() {
            this.removeGroupValueModal = false;
            this.groupValueToRemove = null;
        },

        removeGroupValue() {
            this.$emit(EditableGroupEvent.Remove, {
                groupName: this.groupName,
                groupValue: this.groupValueToRemove?.groupValue,
                editableGroupValueIds: [this.groupValueToRemove?.groupValueId]
            });
            this.$snackbar.success("Group removed successfully!");
        },

        onCollapsibleWidgetInput(isOpened: boolean) {
            if (!isOpened) {
                this.isPoptipActive = false;
            }
        },

        toggleDeleteFeedModal() {
            this.removeFeedModal = !this.removeFeedModal;
        },

        togglePoptip() {
            this.isPoptipActive = !this.isPoptipActive;
        },

        deleteExternalFeed() {
            this.toggleDeleteFeedModal();
            this.$emit("deleteFeed", this.groupName);
        }
    }
};
</script>

<style lang="scss">
@use "@style/variables" as *;

.editable-group {
    &__feed-more,
    &__feed-upload {
        display: none;
    }

    &:hover {
        .editable-group__feed-upload {
            display: flex;
            color: $white;
        }

        .editable-group__feed-more {
            display: flex;
            color: $darktheme100;
        }
    }

    .editable-group__feed-more {
        .ivu-poptip-body {
            padding: 8px;
        }
    }

    .editable-group__action {
        display: flex;
        gap: 8px;
        padding: 7px 16px;
        color: #121619;
        transition: background-color 0.3s;
        border-radius: 8px;

        &:hover {
            background-color: $grey1;
        }
    }

    &__collapsible-widget .collapsible-widget__panel-controls {
        flex: 0 1;
        display: flex;
        justify-content: center;
        align-content: space-between;
        height: 100%;
    }

    &--editing {
        .ivu-collapse > .ivu-collapse-item {
            &.ivu-collapse-item-active {
                .ivu-collapse-header {
                    background: $cmp-dark-active;
                }
            }
        }
    }

    &__body {
        padding: 0;

        .ivu-radio-wrapper,
        .editable-group__controls {
            cursor: pointer;
            margin: 0;
            line-height: 30px;
            padding: 0 20px;

            &.ivu-radio-wrapper-checked,
            &:hover {
                background: $cmp-dark-tertiary-bg-color;

                .show-form {
                    color: $cmp-dark-active;
                }

                .ivu-radio .ivu-radio-inner {
                    border-color: $cmp-dark-active;
                }
            }
        }
    }

    &__add {
        width: 100%;
        display: flex;
        align-content: center;
        justify-content: space-between;
        background: $cmp-dark-tertiary-bg-color;
        border-left: 2px solid $cmp-dark-active;

        .ivu-input-wrapper {
            margin-right: 10px;

            .ivu-input {
                border: 1px solid $darktheme20;
                height: 20px;
                margin: 5px;
            }
        }

        i {
            margin: 0;
            line-height: 30px;
            cursor: pointer;

            &:last-of-type {
                margin-right: 5px;
            }

            &:hover {
                color: $darktheme20;
            }
        }
    }

    &__values {
        width: 100%;
        padding: 0 !important;
    }

    &__controls {
        &--tip {
            font-size: 14px;
            font-style: italic;
            padding-left: 8px;
            color: $cmp-dark-disabled-color;
        }

        &:hover {
            .editable-group__controls--tip {
                color: $cmp-dark-font-color;
            }
        }
    }

    &__value-wrapper {
        position: relative;

        &:hover {
            .editable-group__value-actions {
                display: block;
            }
        }
    }

    &__value-actions {
        display: none;
        position: absolute;
        top: 3px;
        right: 6px;

        i {
            transition: opacity 0.3s;
            opacity: 0.25;
            cursor: pointer;

            &:hover {
                opacity: 1;
            }
        }
    }

    .ivu-collapse-content-box {
        border-bottom: 1px solid $cmp-dark-border-color;
    }

    .ivu-collapse-header {
        .collapsible-widget__panel-controls {
            margin: 0 15px;

            &:hover {
                color: $cmp-dark-active;
            }
        }
    }

    .show-form {
        border-radius: 50%;
        border: 2px solid;
        font-size: 10px;
        color: $blue;
    }
}
.remove-feed__modal {
    .ivu-nodal-header {
        padding: 0;
    }
    .ivu-modal-body {
        padding-top: 0;
        padding-bottom: 0;
    }
    .ivu-modal-footer {
        .ivu-btn {
            &:first-child {
                margin-left: 0;
            }
        }
    }
    .alert-modal {
        &__icon-wrapper {
            display: none;
        }
        &__content-wrapper {
            padding-left: 0;
        }
        &__title {
            font-size: 18px;
            line-height: 28px;
            color: #4d5358;
        }
        &__content {
            padding-right: 50px;
        }
    }
}
</style>
